import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  keyframes,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  DynamicForm,
  Edit as EditIcon,
  FileUpload as ImportIcon,
  History as LegacyIcon,
  LinkRounded as LinkIcon,
  PictureAsPdf,
  AccountTree as RulesIcon,
  Settings as SettingsIcon,
  SmartToy,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { PageTitle, useModal, useToastr } from '@farmshare/ui-components';
import { useApolloClient } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CutsheetQuestionnaire,
  useCutsheetCapabilityDuplicateMutation,
  useCutsheetCapabilityGroupManyQuery,
  useCutsheetCapabilityManyQuery,
  useCutsheetCapabilityMigrateLegacyMutation,
  useCutsheetCapabilityOptionsManyQuery,
  useCutsheetCapabilitySpecificationManyQuery,
  useCutsheetCapabilityUpsertMutation,
  useCutsheetQuestionnaireDeleteMutation,
  useCutsheetQuestionnaireManyLazyQuery,
  useCutsheetQuestionnaireUpsertMutation,
  useParseCutsheetMutation,
} from 'lib/graphql';
import { useUserState } from 'providers/user-state-provider';
import { CutsheetCatalogPage } from './cutsheet-v2';
import { CatalogAndFormsBuilder } from './catalog-and-forms-builder';
import { AssignmentRulesTab } from '../components/assignment-rules-tab';
import { LegacyMappingModal } from '../components/legacy-mapping-modal';
import { CutsheetPdfGenerator } from '../components';
import { ModuleId, useIsModuleActive } from 'lib/modules';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children = null, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

// Animated SmartToy icon keyframes
const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  40% {
    transform: translateY(-15px) rotate(-5deg);
  }
  43% {
    transform: translateY(-15px) rotate(5deg);
  }
  70% {
    transform: translateY(-7px) rotate(-2deg);
  }
`;

interface ImportCutsheetCardProps {
  onFileSelect: (file: File) => Promise<{
    questionnaireName: string;
    cutsFound: number;
    specificationsFound: number;
    primalsFound: number;
  } | null>;
}

const ImportCutsheetCard: FC<ImportCutsheetCardProps> = ({ onFileSelect }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    questionnaireName: string;
    cutsFound: number;
    specificationsFound: number;
    primalsFound: number;
  } | null>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setLoading(true);
      try {
        const uploadResult = await onFileSelect(file);
        setResult(uploadResult);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Card>
      <CardContent>
        {!loading && !result && (
          <Box sx={{ width: 1 }}>
            <input
              accept=".pdf,image/*"
              type="file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="cutsheet-file-input-card"
            />
            <label htmlFor="cutsheet-file-input-card" style={{ width: '100%' }}>
              <Button
                variant="contained"
                component="span"
                fullWidth
                startIcon={<ImportIcon />}
              >
                Choose File
              </Button>
            </label>
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            <SmartToy
              sx={{
                fontSize: 60,
                animation: `${bounce} 2s infinite`,
                color: 'primary.main',
                mb: 2,
                display: 'block',
              }}
            />
            <Typography variant="overline" sx={{ mb: 1 }}>
              AI is analyzing your cutsheet...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Extracting processing capabilities and creating questionnaire.
              This may take several minutes.
            </Typography>
            <LinearProgress sx={{ mt: 2, maxWidth: 300, mx: 'auto' }} />
          </Box>
        )}

        {!loading && !result && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a PDF or image file to begin automated extraction
            </Alert>
            <Typography variant="body2" color="text.secondary">
              The AI will analyze your cutsheet and:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Extract cuts, primals, and specifications"
                  secondary="Automatically identify all processing options"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Create a draft questionnaire"
                  secondary="Generate customer-ready questions"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Update your capabilities catalog"
                  secondary="Sync all options to your processing capabilities"
                />
              </ListItem>
            </List>
          </Box>
        )}

        {!loading && result && (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" icon={false} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Successfully imported cutsheet!
              </Typography>
              <Typography variant="body2">
                Created questionnaire:{' '}
                <strong>{result.questionnaireName}</strong>
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {result.primalsFound}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Primals Found
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {result.cutsFound}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cuts Found
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {result.specificationsFound}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Specifications Found
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

interface CutsheetSpeciesManagementProps {
  /** The capability (species) to manage. Resolved by the parent from the URL species segment. */
  capabilityIdOverride: string;
  /** Species dropdown rendered inline with the tab bar */
  speciesSelector?: React.ReactNode;
}

/**
 * Species-specific Cut Sheet Management
 *
 * Tabbed interface for managing a specific animal species:
 * 1. Cuts & Options - Configure processing capabilities (groups, cuts, trim, specs)
 * 2. Cut Sheet Forms - Manage customer-facing questionnaires
 * 3. Assignment Rules
 * 4. Print Settings
 * 5. Settings
 * @param root0
 * @param root0.capabilityIdOverride
 * @param root0.speciesSelector
 */
export const CutsheetSpeciesManagement: FC<CutsheetSpeciesManagementProps> = ({
  capabilityIdOverride,
  speciesSelector,
}) => {
  // Always driven by the `capabilityIdOverride` prop from the landing page;
  // the URL carries species, not capabilityId, so no useParams lookup here.
  const capabilityId = capabilityIdOverride;
  const navigate = useNavigate();
  const { save, ask, info } = useModal();
  const { push } = useToastr();
  const { vendor, isAdmin } = useUserState();
  const apolloClient = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCapabilitiesV1Active = useIsModuleActive(ModuleId.CAPABILITIES_V1);

  const TAB_KEYS = [
    'catalog',
    'builder',
    'rules',
    'print',
    'settings',
  ] as const;
  type TabKey = (typeof TAB_KEYS)[number];

  const tabParam = searchParams.get('tab') as TabKey | null;
  const currentTab =
    tabParam && TAB_KEYS.includes(tabParam) ? TAB_KEYS.indexOf(tabParam) : 0;

  const setCurrentTab = (index: number) => {
    const next = new URLSearchParams(searchParams);
    const key = TAB_KEYS[index] ?? TAB_KEYS[0];
    if (key === TAB_KEYS[0]) next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next);
  };

  // Mobile: show icon-only tabs (with tooltips for accessibility)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch all active capabilities for this vendor, then find the specific one by ID.
  // The many-query has no _id filter, so we do a client-side match below.
  const { data: capabilityData, refetch: refetchCapability } =
    useCutsheetCapabilityManyQuery({
      variables: {
        vendorId: vendor?._id,
        isActive: true,
      },
      skip: !capabilityId || !vendor?._id,
    });

  // Find the specific capability that matches the route param
  const capability =
    capabilityData?.CutsheetCapabilityByVendorAndAnimal?.find(
      (c) => c?._id === capabilityId,
    ) ?? null;

  // Derive a species label for display purposes (e.g. "Beef" in page titles).
  // A capability can cover multiple species; join them for descriptive text.
  const species = capability?.species ?? '';

  // The capabilityId comes directly from the URL — no derivation needed.
  const selectedCapabilityId = capabilityId;

  const questionnaireQuery = useCutsheetQuestionnaireManyLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // Trigger questionnaire fetch when capability is available
  useEffect(() => {
    if (selectedCapabilityId) {
      questionnaireQuery[0]({
        variables: { capabilityId: selectedCapabilityId },
      });
    }
  }, [selectedCapabilityId]);

  const { data: capabilityOptionsData } = useCutsheetCapabilityOptionsManyQuery(
    {
      variables: {
        capabilityId: selectedCapabilityId!,
        isActive: true,
      },
      skip: !selectedCapabilityId || currentTab !== 3,
    },
  );

  const { data: primalsData } = useCutsheetCapabilityGroupManyQuery({
    variables: {
      capabilityId: selectedCapabilityId!,
      isActive: true,
    },
    skip: !selectedCapabilityId || currentTab !== 3,
  });

  const { data: specificationsData } =
    useCutsheetCapabilitySpecificationManyQuery({
      variables: {
        capabilityId: selectedCapabilityId!,
        isActive: true,
      },
      skip: !selectedCapabilityId || currentTab !== 3,
    });

  // Build default form values for PDF preview from capability data
  const defaultFormValues = useMemo(() => {
    // `capability` is already derived above; re-alias inside memo for clarity
    const cap = capability;
    const primals = primalsData?.cutsheetGroupsByVendorAndAnimal || [];
    const options =
      capabilityOptionsData?.cutsheetCapabilityOptionsByVendorAndAnimal || [];
    const specifications =
      specificationsData?.cutsheetSpecificationsByVendorAndAnimal || [];

    if (!cap || primals.length === 0) {
      return {
        primalGroups: [],
        specifications: [],
        trim: [],
        extras: [],
      };
    }

    // Sort all lists by sortOrder so the PDF preview reflects the user-defined ordering.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sortBySortOrder = (arr: any[]) =>
      [...arr].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));

    const sortedPrimals = sortBySortOrder(primals);

    // Filter options by type and sort each group independently
    const cuts = sortBySortOrder(
      options.filter((opt) => opt?.optionType === 'cut'),
    );
    const trims = sortBySortOrder(
      options.filter((opt) => opt?.optionType === 'trim'),
    );
    const extras = sortBySortOrder(
      options.filter(
        (opt) => opt?.optionType !== 'cut' && opt?.optionType !== 'trim',
      ),
    );
    const sortedSpecifications = sortBySortOrder(specifications);

    // Build primal groups from capability data — one group per primal.
    // Using array index as the group order since the array is already sorted.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const primalGroups = sortedPrimals.map((primal: any, index: number) => ({
      name: '',
      order: index,
      primals: [
        {
          _id: primal?._id,
          name: primal?.title || '',
          order: index,
          // Cuts are pre-sorted; pass sortOrder as `order` so buildCutsheetPreviewData
          // can re-sort them correctly via sortBy(cuts, 'order').
          cuts: cuts
            .filter((cut) => cut?.groupId === primal?._id)
            .map((cut) => ({
              _id: cut?._id,
              name: cut?.printedText || cut?.title || '',
              order: cut?.sortOrder ?? 0,
              isDeleted: false,
            })),
          isDeleted: false,
        },
      ],
      isDeleted: false,
    }));

    return {
      primalGroups,
      specifications: sortedSpecifications.map((spec) => ({
        _id: spec?._id,
        name: spec?.printedText || spec?.title || '',
        value: spec?.defaultValue || '',
        notes: '',
      })),
      trim: trims.map((trim) => ({
        name: trim?.printedText || trim?.title || '',
        weightInLb: 0,
      })),
      extras: extras.map((extra) => ({
        name: extra?.printedText || extra?.title || '',
        weightInLb: 0,
      })),
    };
  }, [capability, capabilityOptionsData, primalsData, specificationsData]);

  // Species options from vendor's enabled animal settings
  const enabledSpeciesOptions = useMemo(
    () =>
      vendor?.processorSettings?.animalSettings
        ?.filter((s) => s?.isEnabled)
        .map((s) => s!.species as string) ?? [],
    [vendor],
  );

  // Mutations
  const [upsertCapability] = useCutsheetCapabilityUpsertMutation();
  const [createQuestionnaire] = useCutsheetQuestionnaireUpsertMutation();
  const [deleteQuestionnaire] = useCutsheetQuestionnaireDeleteMutation();
  const [parseCutsheet, { loading: parsingCutsheet }] =
    useParseCutsheetMutation();
  const [duplicateCapability] = useCutsheetCapabilityDuplicateMutation();
  const [migrateLegacy] = useCutsheetCapabilityMigrateLegacyMutation();

  // Other capabilities for the "Copy from" dropdown (exclude the current one)
  const otherCapabilities = useMemo(
    () =>
      (capabilityData?.CutsheetCapabilityByVendorAndAnimal ?? []).filter(
        (c) => c?._id !== selectedCapabilityId,
      ),
    [capabilityData, selectedCapabilityId],
  );

  const handleCopyFromCapability = () => {
    let selectedSourceId: string | null = null;
    let includeQuestionnaires = true;
    let includeAssignmentRules = true;

    const CopyFromCard: FC = () => {
      const [sourceId, setSourceId] = useState<string | null>(null);
      const [copyQ, setCopyQ] = useState(true);
      const [copyR, setCopyR] = useState(true);
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState<string | null>(null);
      const [summary, setSummary] = useState<{
        groupsCopied: number;
        optionsCopied: number;
        specificationsCopied: number;
        questionnairesCopied: number;
        assignmentRulesCopied: number;
      } | null>(null);

      // Sync to outer scope for the modal
      useEffect(() => {
        selectedSourceId = sourceId;
        includeQuestionnaires = copyQ;
        includeAssignmentRules = copyR;
      }, [sourceId, copyQ, copyR]);

      const handleCopy = async () => {
        if (!sourceId || !selectedCapabilityId) return;
        setLoading(true);
        setError(null);
        try {
          const res = await duplicateCapability({
            variables: {
              sourceCapabilityId: sourceId,
              targetCapabilityId: selectedCapabilityId,
              includeQuestionnaires: copyQ,
              includeAssignmentRules: copyR,
            },
          });
          const s = res.data?.CutsheetCapabilityDuplicate?.summary;
          setSummary({
            groupsCopied: s?.groupsCopied ?? 0,
            optionsCopied: s?.optionsCopied ?? 0,
            specificationsCopied: s?.specificationsCopied ?? 0,
            questionnairesCopied: s?.questionnairesCopied ?? 0,
            assignmentRulesCopied: s?.assignmentRulesCopied ?? 0,
          });
          // Refresh every mounted query so the catalog panel, form picker,
          // assignment rules tab, etc. all reflect the new target-side data.
          await apolloClient.refetchQueries({ include: 'active' });
        } catch (e: any) {
          setError(e?.message ?? 'Failed to copy capability data.');
        } finally {
          setLoading(false);
        }
      };

      if (summary) {
        return (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6">Copy Complete</Typography>
            </Box>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`${summary.groupsCopied} group${summary.groupsCopied === 1 ? '' : 's'} copied`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`${summary.optionsCopied} cut / trim / extra${summary.optionsCopied === 1 ? '' : 's'} copied`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`${summary.specificationsCopied} specification${summary.specificationsCopied === 1 ? '' : 's'} copied`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`${summary.questionnairesCopied} questionnaire${summary.questionnairesCopied === 1 ? '' : 's'} copied`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`${summary.assignmentRulesCopied} assignment rule${summary.assignmentRulesCopied === 1 ? '' : 's'} copied`}
                />
              </ListItem>
            </List>
          </Stack>
        );
      }

      return (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Catalog items (groups, cuts, trims, extras, specifications) are
            always copied. Questionnaires and assignment rules are optional. The
            target capability must be empty.
          </Alert>
          <Autocomplete
            options={otherCapabilities}
            getOptionLabel={(opt) =>
              `${opt?.name ?? 'Unnamed'} (${opt?.species ?? ''})`
            }
            value={otherCapabilities.find((c) => c?._id === sourceId) ?? null}
            onChange={(_, val) => setSourceId(val?._id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Source Capability Set"
                placeholder="Select a capability set to copy from"
                size="small"
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={copyQ}
                onChange={(e) => {
                  const { checked } = e.target;
                  setCopyQ(checked);
                  if (!checked) setCopyR(false);
                }}
              />
            }
            label="Copy questionnaires"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={copyR}
                disabled={!copyQ}
                onChange={(e) => setCopyR(e.target.checked)}
              />
            }
            label="Copy assignment rules"
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            onClick={handleCopy}
            disabled={!sourceId || loading}
          >
            {loading ? 'Copying…' : 'Copy'}
          </Button>
        </Stack>
      );
    };

    info({
      type: 'info',
      title: 'Copy From Another Capability Set',
      footer: null,
      body: <CopyFromCard />,
    });
  };

  const handleMigrateLegacy = () => {
    const MigrateLegacyCard: FC = () => {
      const [inspectionLevel, setInspectionLevel] = useState<string>('');
      const [enabledOnly, setEnabledOnly] = useState(true);
      const [deactivateUnused, setDeactivateUnused] = useState(false);
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState<string | null>(null);
      const [lastLevel, setLastLevel] = useState<string>('');
      const [summary, setSummary] = useState<{
        inspectionLevelsMigrated: string[];
        groupsCreated: number;
        groupsReused: number;
        cutsCreated: number;
        cutsReused: number;
        trimsCreated: number;
        trimsReused: number;
        specificationsCreated: number;
        specificationsReused: number;
        questionnairesCreated: number;
        questionnairesReused: number;
        assignmentRulesAdded: number;
      } | null>(null);

      const migratedLevels = useMemo(
        () =>
          new Set(
            (capability?.legacyInspectionLevelMap ?? [])
              .map((e) => e?.inspectionLevel as string | undefined)
              .filter(Boolean) as string[],
          ),
        [],
      );

      const levelOptions = [
        { value: 'exempt', label: 'Custom Exempt' },
        { value: 'state', label: 'State Inspected' },
        { value: 'usda', label: 'USDA' },
      ];

      const handleMigrate = async () => {
        if (!selectedCapabilityId) return;
        setLoading(true);
        setError(null);
        try {
          const res = await migrateLegacy({
            variables: {
              capabilityId: selectedCapabilityId,
              enabledOnly: inspectionLevel ? false : enabledOnly,
              deactivateUnusedCuts: deactivateUnused,
              inspectionLevel: inspectionLevel || undefined,
            },
          });
          const s = res.data?.CutsheetCapabilityMigrateLegacy?.summary;
          setSummary({
            inspectionLevelsMigrated: (s?.inspectionLevelsMigrated ??
              []) as string[],
            groupsCreated: s?.groupsCreated ?? 0,
            groupsReused: s?.groupsReused ?? 0,
            cutsCreated: s?.cutsCreated ?? 0,
            cutsReused: s?.cutsReused ?? 0,
            trimsCreated: s?.trimsCreated ?? 0,
            trimsReused: s?.trimsReused ?? 0,
            specificationsCreated: s?.specificationsCreated ?? 0,
            specificationsReused: s?.specificationsReused ?? 0,
            questionnairesCreated: s?.questionnairesCreated ?? 0,
            questionnairesReused: s?.questionnairesReused ?? 0,
            assignmentRulesAdded: s?.assignmentRulesAdded ?? 0,
          });
          setLastLevel(inspectionLevel);
          refetchCapability();
          questionnaireQuery[0]({
            variables: { capabilityId: selectedCapabilityId },
          });
        } catch (e: any) {
          setError(e?.message ?? 'Failed to migrate legacy data.');
        } finally {
          setLoading(false);
        }
      };

      const handleMigrateAnother = () => {
        setSummary(null);
        setError(null);
        setInspectionLevel('');
      };

      if (summary) {
        const doneLabel =
          levelOptions.find((l) => l.value === lastLevel)?.label ??
          'All levels';

        const rows: Array<{ label: string; created: number; reused: number }> =
          [
            {
              label: 'Groups',
              created: summary.groupsCreated,
              reused: summary.groupsReused,
            },
            {
              label: 'Cuts',
              created: summary.cutsCreated,
              reused: summary.cutsReused,
            },
            {
              label: 'Trims',
              created: summary.trimsCreated,
              reused: summary.trimsReused,
            },
            {
              label: 'Specifications',
              created: summary.specificationsCreated,
              reused: summary.specificationsReused,
            },
            {
              label: 'Questionnaires',
              created: summary.questionnairesCreated,
              reused: summary.questionnairesReused,
            },
          ];

        return (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6">Migration Complete</Typography>
              <Typography variant="body2" color="text.secondary">
                Migrated: <strong>{doneLabel}</strong>
              </Typography>
            </Box>

            <Stack spacing={0.5} sx={{ px: 1 }}>
              {rows.map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  justifyContent="space-between"
                  sx={{ fontSize: 14 }}
                >
                  <Typography variant="body2">{row.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{row.created}</strong> created
                    {row.reused > 0 ? `, ${row.reused} reused` : ''}
                  </Typography>
                </Stack>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ fontSize: 14 }}
              >
                <Typography variant="body2">Assignment rules</Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{summary.assignmentRulesAdded}</strong> added
                </Typography>
              </Stack>
            </Stack>

            <Button variant="outlined" onClick={handleMigrateAnother}>
              Migrate Another Level
            </Button>
          </Stack>
        );
      }

      return (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Matching groups, cuts, trims, and specs by name are reused. The
            legacy mapping accumulates across runs — migrate one inspection
            level at a time and the mapping builds as you go.
          </Alert>
          <TextField
            select
            label="Inspection Level"
            value={inspectionLevel}
            onChange={(e) => setInspectionLevel(e.target.value)}
            helperText={
              inspectionLevel
                ? migratedLevels.has(inspectionLevel)
                  ? 'Already migrated — re-running will refresh its mapping.'
                  : 'Will migrate only this inspection level.'
                : 'Leave blank to migrate every available level.'
            }
            size="small"
          >
            <MenuItem value="">
              <em>All inspection levels</em>
            </MenuItem>
            {levelOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
                {migratedLevels.has(opt.value) ? ' — already migrated' : ''}
              </MenuItem>
            ))}
          </TextField>
          {!inspectionLevel && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledOnly}
                  onChange={(e) => setEnabledOnly(e.target.checked)}
                />
              }
              label="Only migrate enabled inspection levels"
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={deactivateUnused}
                onChange={(e) => setDeactivateUnused(e.target.checked)}
              />
            }
            label="Deactivate unused cuts"
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            onClick={handleMigrate}
            disabled={loading}
          >
            {loading ? 'Migrating…' : 'Migrate'}
          </Button>
        </Stack>
      );
    };

    info({
      type: 'info',
      title: 'Migrate Legacy Capabilities',
      footer: null,
      body: <MigrateLegacyCard />,
    });
  };

  const handleConfigureLegacyMap = () => {
    if (!selectedCapabilityId) return;
    const enabledLevels = vendor?.processorSettings?.animalSettings
      ?.find(
        (s) => s?.isEnabled && (s?.species as string) === (species as string),
      )
      ?.inspectionLevels?.filter(Boolean) as string[] | undefined;
    info({
      type: 'info',
      title: 'Configure Legacy Cutsheet Mapping',
      footer: null,
      body: (
        <LegacyMappingModal
          capabilityId={selectedCapabilityId}
          questionnaires={questionnaires}
          existingMap={capability?.legacyInspectionLevelMap}
          enabledInspectionLevels={enabledLevels}
          onComplete={() => refetchCapability()}
        />
      ),
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleCreateQuestionnaire = async () => {
    try {
      const result = await createQuestionnaire({
        variables: {
          capabilityId: selectedCapabilityId!,
          name: `${species?.charAt(0).toUpperCase()}${species?.slice(1)} Questionnaire`,
          description: `Customer questionnaire for ${species} processing`,
          questions: [],
          status: 'draft',
        },
      });

      push({
        title: 'Success',
        body: 'Created new questionnaire',
        bg: 'success',
      });

      if (result.data?.cutsheetQuestionnaireUpsert?._id) {
        navigate(
          `/cutsheets/questionnaire-builder?id=${result.data.cutsheetQuestionnaireUpsert._id}`,
        );
      }
    } catch (error) {
      push({
        title: 'Error',
        body: 'Failed to create questionnaire',
        bg: 'error',
      });
    }
  };

  const handleImportCutsheet = () => {
    const handleFileUpload = async (file: File) => {
      if (!selectedCapabilityId) {
        push({
          title: 'Error',
          body: 'No capability found for this species. Please create one first.',
          bg: 'error',
        });
        return null;
      }

      try {
        const result = await parseCutsheet({
          variables: {
            file,
            capabilityId: selectedCapabilityId,
          },
        });

        if (result.data?.parseCutsheet) {
          // Return the result for display
          return {
            questionnaireName:
              result.data.parseCutsheet.questionnaire.name || 'Untitled',
            cutsFound: result.data.parseCutsheet.catalog.cuts?.length || 0,
            specificationsFound:
              result.data.parseCutsheet.catalog.specifications?.length || 0,
            primalsFound:
              result.data.parseCutsheet.catalog.primals?.length || 0,
          };
        }
        return null;
      } catch (error) {
        push({
          title: 'Error',
          body: 'Failed to parse cutsheet. Please try again.',
          bg: 'error',
        });
        return null;
      }
    };

    info({
      type: 'info',
      title: 'Import Cutsheet',
      footer: null,
      body: <ImportCutsheetCard onFileSelect={handleFileUpload} />,
    });
  };

  if (!capabilityId) {
    return (
      <Typography variant="h6" color="error">
        Capability ID parameter is required
      </Typography>
    );
  }

  const questionnaires = (
    questionnaireQuery[1].data?.cutsheetQuestionnairesByVendorAndAnimal ?? []
  ).filter(Boolean) as CutsheetQuestionnaire[];

  return (
    <>
      {/* Mobile: species selector on its own row as the page's primary context */}
      {!!speciesSelector && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, textTransform: 'capitalize' }}
          >
            Cut Sheets
          </Typography>
          {speciesSelector}
        </Box>
      )}

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          // Allow tabs to scroll horizontally on small screens instead of
          // squishing labels — and free up full width since the species
          // selector has moved out of this row on mobile.
          overflowX: { xs: 'auto', md: 'visible' },
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            flex: 1,
            // Align the first tab's left edge with grid content below (on
            // desktop) — MUI Tab has default horizontal padding that makes
            // its icon/label sit inset from the container edge, which
            // mismatches the sidebar/catalog column's x=0 start.
            '& .MuiTab-root:first-of-type': {
              pl: { xs: 1.5, md: 0 },
            },
          }}
        >
          <Tooltip title={isMobile ? 'Catalog' : ''} placement="bottom">
            <Tab
              icon={<TuneIcon />}
              label={isMobile ? undefined : 'Catalog'}
              iconPosition="start"
              aria-label="Catalog"
            />
          </Tooltip>
          <Tooltip
            title={isMobile ? 'Cut Sheet Builder' : ''}
            placement="bottom"
          >
            <Tab
              icon={<DynamicForm />}
              label={isMobile ? undefined : 'Cut Sheet Builder'}
              iconPosition="start"
              aria-label="Cut Sheet Builder"
            />
          </Tooltip>

          <Tooltip
            title={isMobile ? 'Assignment Rules' : ''}
            placement="bottom"
          >
            <Tab
              icon={<RulesIcon />}
              label={isMobile ? undefined : 'Assignment Rules'}
              iconPosition="start"
              aria-label="Assignment Rules"
            />
          </Tooltip>
          <Tooltip title={isMobile ? 'Print Settings' : ''} placement="bottom">
            <Tab
              icon={<PictureAsPdf />}
              label={isMobile ? undefined : 'Print Settings'}
              iconPosition="start"
              aria-label="Print Settings"
            />
          </Tooltip>
          <Tooltip title={isMobile ? 'Settings' : ''} placement="bottom">
            <Tab
              icon={<SettingsIcon />}
              label={isMobile ? undefined : 'Settings'}
              iconPosition="start"
              aria-label="Settings"
            />
          </Tooltip>
        </Tabs>
        {/* Desktop-only: keep species selector inline with tabs */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          {speciesSelector}
        </Box>
      </Box>

      <TabPanel value={currentTab} index={0}>
        {vendor?._id && selectedCapabilityId && (
          <CutsheetCatalogPage
            vendorId={vendor._id}
            capabilityId={selectedCapabilityId}
            onNavigateToQuestionnaire={() => setCurrentTab(0)}
          />
        )}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Unified Catalog + Form Builder */}
        {vendor?._id && selectedCapabilityId && (
          <CatalogAndFormsBuilder
            vendorId={vendor._id}
            capabilityId={selectedCapabilityId}
          />
        )}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {selectedCapabilityId && (
          <AssignmentRulesTab capabilityId={selectedCapabilityId} />
        )}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {capability && (
          <Box sx={{ py: 3 }}>
            <CutsheetPdfGenerator
              formValues={defaultFormValues}
              capability={capability}
            />
          </Box>
        )}
        {!capability && (
          <Card elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Stack spacing={2} alignItems="center">
              <PictureAsPdf sx={{ fontSize: 64, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                No Cuts Configured
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set up your cuts and options first to preview PDF templates.
              </Typography>
              <Button variant="outlined" onClick={() => setCurrentTab(0)}>
                Go to Catalog
              </Button>
            </Stack>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <Box sx={{ py: 3, maxWidth: 600 }}>
          {isAdmin && (
            <>
              {/* Import Cutsheet */}
              <Typography variant="h5" gutterBottom>
                Import Cutsheet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a PDF or image of an existing cutsheet for AI-powered
                extraction of cuts, primals, and specifications.
              </Typography>
              <Button
                variant="contained"
                startIcon={<ImportIcon />}
                onClick={handleImportCutsheet}
                sx={{ mb: 4 }}
              >
                Import Cutsheet
              </Button>

              <Divider sx={{ mb: 4 }} />
            </>
          )}

          {/* Copy from another capability set */}
          <Typography variant="h5" gutterBottom>
            Copy From Another Species
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Duplicate questionnaires and assignment rules from another
            species&apos; capability set into this one.
          </Typography>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={handleCopyFromCapability}
            disabled={otherCapabilities.length === 0}
            sx={{ mb: 4 }}
          >
            Copy From Capability Set
          </Button>

          <Divider sx={{ mb: 4 }} />

          {isCapabilitiesV1Active && (
            <>
              {/* Migrate legacy capabilities */}
              <Typography variant="h5" gutterBottom>
                Migrate Legacy Capabilities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Import groups, cuts, specifications, questionnaires, and
                assignment rules from the legacy processor capability system, as
                well as mapping over existing cutsheets.
              </Typography>
              <Stack gap={2} direction="row">
                <Button
                  variant="contained"
                  startIcon={<LegacyIcon />}
                  onClick={handleMigrateLegacy}
                  sx={{ mb: 4 }}
                >
                  Migrate Legacy Data
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleConfigureLegacyMap}
                  disabled={questionnaires.length === 0}
                  sx={{ mb: 4 }}
                >
                  Configure Mapping
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </TabPanel>
    </>
  );
};
